import * as React from 'react';
import { Alert, Button, Modal } from '@ixicare/ui';
import { useMutation } from '@tanstack/react-query';
import classnames from 'classnames';
import { Beacon, Device, DeviceType, Gateway } from '@ixicare/resources';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';
import { humaniseErrors } from '~utils/error';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import styles from '~/components/ActionBar/ActionBar.module.css';

const MAX_CHIPS = 7;

interface ResetModalProps {
  devices: DBGWithType[];
  onClose: () => void;
  onRefresh: () => void;
}

const resetOne = (device: DBGWithType) => {
  if (device.type === DeviceType.GATEWAY) return Gateway.resetGateway(Number(device.id));
  if (device.type === DeviceType.BEACON) return Beacon.resetBeacon(Number(device.id));
  return Device.resetDevice(Number(device.id));
};

export function ResetModal({ devices, onClose, onRefresh }: ResetModalProps) {
  const shownDevices = devices.slice(0, MAX_CHIPS);
  const overflow = devices.length - shownDevices.length;

  const mutation = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(devices.map(resetOne));
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length === results.length) throw failures[0].reason;
      return { failures };
    },
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      {/* The heading reuses the action label rather than having a `title` key of its
          own — it would only ever be the same sentence, in 45 languages. */}
      <h3 className="text-center mb-10">{t('device.reset.action')}</h3>
      <ul className={styles.chips}>
        {shownDevices.map((device) => (
          <li className={classnames('text-center', 'mb-10', styles.chip)} key={device.macAddress}>
            {device.macAddress} - {device.description}
          </li>
        ))}
        {overflow > 0 && <li className={classnames('text-center', styles.chip)}>+{overflow}</li>}
      </ul>
      <p className="text-muted text-center">{t('device.reset.explanation')}</p>
      {mutation.error && <Alert type="error">{humaniseErrors(mutation.error as Error).title}</Alert>}
      <Spacer size={32} />
      <Button className="mb-15" isBlock loading={mutation.isPending} type="button" onClick={() => mutation.mutate()}>
        {t('device.reset.confirm')}
      </Button>
      <Button onClick={() => onClose()} disabled={mutation.isPending} isBlock buttonType="link">
        {t('global.cancel')}
      </Button>
    </Modal>
  );
}
